import { defineStore } from 'pinia';
import { api, ApiBaseUrl } from '../api';
import { Organization } from '@/interface/Organization';
import { User } from '@/interface/User';

/** Auth Service */
export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: {} as User,
    avatar: '',
    permissions: [] as string[],
    roles: [] as string[],
    error: null as string | null,
    loading: false,
    loaded: false,
    organization: {} as Organization,
  }),
  getters: {
    getRoles(state) {
      return state.roles;
    },
    getUser(state) {
      return state.user;
    },
    getPermissions(state) {
      return state.permissions;
    },
    getOrganization(state) {
      return state.organization;
    },
  },
  actions: {
    canAny(perm: string[]) {
      return perm.some((item) => this.can(item));
    },
    can(perm: string) {
      return this.permissions.includes(perm);
    },

    setUser(user: User) {
      this.user = user;
    },
    setRoles(roles: []) {
      this.roles = roles;
    },
    setPermissions(perms: []) {
      this.permissions = perms;
    },
    setOrganization(organization: Organization) {
      this.organization = organization;
    },
    async switchOrganisation(organisation: Record<string, any>) {
      await api.post(`/organisations/${organisation.id}/switch`);
    },
    async validateToken(provider: string,code: string){
      await api.get('/sanctum/csrf-cookie', { baseURL: ApiBaseUrl });
      const res=await api.post(`/auth/${provider}/callback`,{ code });
      return res.data;
    },
    async fetchAuthUser() {
      await api.get('/sanctum/csrf-cookie', { baseURL: ApiBaseUrl });
      this.loading = true;
      this.error = null;

      try {
        const response = await api.get('user');
        this.setUser(response.data?.user);
        this.setRoles(response.data?.roles);
        this.setPermissions(response.data?.permissions);
        this.setOrganization(response.data?.organization);
        return response.data;
      } catch (error: any) {
        this.error = error.response?.data || 'Error fetching user';
        throw error;
      } finally {
        this.loading = false;
        this.loaded = true;
      }
    },
    async logout() {
      this.loading = true;
      this.error = null;

      try {
        const response = await api.post('logout');
        return response.data;
      } catch (error: any) {
        this.error = error.response?.data || 'Error fetching user';
        throw error;
      } finally {
        this.loading = false;
        // this.loaded = true;
      }
    },
    resetState() {
      this.user = {} as User;
      this.avatar = '';
      this.permissions = [];
      this.roles = [];
      this.organization = {} as Organization;
      this.error = null;
      this.loading = false;
    },
    clearState() {
      const storageKey = import.meta.env.VITE_APP_WEBSTORAGE_AUTH_NAMESPACE || 'resma';
      localStorage.removeItem(storageKey);
      localStorage.removeItem('auth');
      this.resetState();
      this.$reset();
    },
  },
});
